import { useState } from 'react';
import { MapPin, LocateFixed, Search, Check } from 'lucide-react';
import { Input } from './Input';
import { Button } from './Button';
import { toast } from './Toast';

export interface LocationValue {
  latitude: number;
  longitude: number;
  address?: string;
}

interface Props {
  value: LocationValue | null;
  onChange: (v: LocationValue) => void;
}

interface Hit {
  label: string;
  lat: number;
  lon: number;
}

// Location picker — address search via OpenStreetMap Nominatim (no API key,
// biased to South Africa) plus the browser Geolocation API. Raw lat/lng entry
// is kept behind an "advanced" toggle as a fallback; real users should never
// need to type coordinates.
export function LocationPicker({ value, onChange }: Props) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<Hit[]>([]);
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [advanced, setAdvanced] = useState(false);

  const search = async () => {
    const q = query.trim();
    if (q.length < 3) {
      toast.error('Type at least 3 characters to search.');
      return;
    }
    setSearching(true);
    try {
      const url =
        'https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1' +
        `&countrycodes=za&limit=5&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
      if (!res.ok) throw new Error('Search failed');
      const data = (await res.json()) as Array<{ display_name: string; lat: string; lon: string }>;
      const mapped = data.map((d) => ({
        label: d.display_name,
        lat: parseFloat(d.lat),
        lon: parseFloat(d.lon),
      }));
      setHits(mapped);
      if (mapped.length === 0) toast.error('No matches — try a landmark or suburb.');
    } catch {
      toast.error('Search is unavailable. Enter coordinates manually below.');
      setAdvanced(true);
    } finally {
      setSearching(false);
    }
  };

  const choose = (hit: Hit) => {
    onChange({ latitude: hit.lat, longitude: hit.lon, address: hit.label });
    setHits([]);
    setQuery(hit.label);
  };

  const useCurrent = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by your browser.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        let address = value?.address;
        try {
          const res = await fetch(
            'https://nominatim.openstreetmap.org/reverse?format=jsonv2' +
              `&lat=${latitude}&lon=${longitude}`,
            { headers: { 'Accept-Language': 'en' } },
          );
          if (res.ok) address = (await res.json())?.display_name ?? address;
        } catch {
          /* reverse geocode is best-effort */
        }
        onChange({ latitude, longitude, address });
        setLocating(false);
        toast.success('Location captured.');
      },
      (err) => {
        setLocating(false);
        toast.error(err.message);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search
            size={15}
            className="pointer-events-none absolute left-0 top-3 text-makhe-muted"
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                search();
              }
            }}
            placeholder="Search address, suburb or campus…"
            className="w-full border-0 border-b border-makhe-ink/40 bg-transparent py-2 pl-6 text-makhe-ink placeholder:text-makhe-muted/60 focus:border-makhe-ink focus:outline-none"
          />
        </div>
        <Button type="button" variant="secondary" onClick={search} loading={searching}>
          Search
        </Button>
      </div>

      {hits.length > 0 && (
        <ul className="divide-y divide-makhe-line overflow-hidden rounded-xl border border-makhe-line bg-makhe-paper">
          {hits.map((hit, i) => (
            <li key={`${hit.lat}-${hit.lon}-${i}`}>
              <button
                type="button"
                onClick={() => choose(hit)}
                className="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm hover:bg-makhe-line/30"
              >
                <MapPin size={14} className="mt-0.5 shrink-0 text-makhe-primary" />
                <span className="line-clamp-2">{hit.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={useCurrent}
        disabled={locating}
        className="inline-flex items-center gap-2 text-sm text-makhe-primary disabled:opacity-60"
      >
        <LocateFixed size={15} />
        {locating ? 'Locating…' : 'Use my current location'}
      </button>

      {value && (
        <div className="flex items-start gap-2 rounded-xl bg-makhe-forest/10 p-3 text-sm text-makhe-ink">
          <Check size={15} className="mt-0.5 shrink-0 text-makhe-forest" />
          <span>{value.address ?? `${value.latitude.toFixed(5)}, ${value.longitude.toFixed(5)}`}</span>
        </div>
      )}

      <button
        type="button"
        onClick={() => setAdvanced((a) => !a)}
        className="text-[11px] uppercase tracking-widest text-makhe-muted hover:text-makhe-ink"
      >
        {advanced ? 'Hide' : 'Enter coordinates manually'}
      </button>

      {advanced && (
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Latitude"
            type="number"
            step="any"
            value={value?.latitude ?? ''}
            onChange={(e) =>
              onChange({
                latitude: parseFloat(e.target.value) || 0,
                longitude: value?.longitude ?? 0,
                address: value?.address,
              })
            }
          />
          <Input
            label="Longitude"
            type="number"
            step="any"
            value={value?.longitude ?? ''}
            onChange={(e) =>
              onChange({
                latitude: value?.latitude ?? 0,
                longitude: parseFloat(e.target.value) || 0,
                address: value?.address,
              })
            }
          />
        </div>
      )}
    </div>
  );
}
